import React, { useState } from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  Label,
  Alert,
  Button,
  Spinner,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { formatDateToDDMMYYYY } from '../../../../../../utils/dateformatter';
import { api } from '../../../../../../config';
import { getAuthToken } from '../../../../../../auth/tokenvalidation';
import {
  displayValue,
  formatProposalDeadline,
  getContractPeriodProps,
  getContractTypeLabel,
  getProcurementTypeLabel,
  canAccessEvaluationOperationalData,
  getEvaluationOperationalLockReason,
  isHpsAvailable,
} from '../../../../utils/tenderEvaluationHelpers';
import HpsParcelSection from '../../../../../Hps/component/HpsParcel';
import TenderNotClosedAlert from '../Components/TenderNotClosedAlert';
import '../../../../../../App.css';

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.parentNode.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const InfoField = ({ label, value }) => (
  <Col md={6} lg={4}>
    <div className='border-start border-2 border-primary ps-3'>
      <Label className='fw-semibold small text-dark mb-1'>{label}</Label>
      <p className='mb-0 text-dark fw-medium'>{displayValue(value)}</p>
    </div>
  </Col>
);

const TabHPS = ({
  procurement,
  procId,
  isLoadingProcurement,
  parcels = [],
  isLoadingParcels = false,
}) => {
  const [isExportingExcel, setIsExportingExcel] = useState(false);
  const procurementId = procurement?.id || procId;
  const lockReason = getEvaluationOperationalLockReason(procurement);
  const canAccessData = canAccessEvaluationOperationalData(procurement);
  const hpsAvailable = isHpsAvailable(procurement);
  const periodProps = getContractPeriodProps(procurement);

  const exportParcelHpsToExcel = async () => {
    const token = getAuthToken();
    if (!token) {
      toast.error('Authentication required. Please login again.');
      return false;
    }
    if (!procurementId) {
      toast.error('Procurement ID is required.');
      return false;
    }

    setIsExportingExcel(true);
    try {
      const response = await fetch(
        `${api.API_URL}/procurement/exportToExcelParcelRequestHpsMarket?procurementId=${procurementId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        let message = `Failed to export parcel data (${response.status}).`;
        try {
          const errorData = await response.json();
          message = errorData?.message || message;
        } catch {
          // response body bukan JSON
        }
        throw new Error(message);
      }

      const blob = await response.blob();
      downloadBlob(blob, 'Parcel_HPS_Market.xlsx');
      return true;
    } catch (error) {
      const message =
        error?.message || 'Failed to export parcel data to Excel.';
      toast.error(message);
      return false;
    } finally {
      setIsExportingExcel(false);
    }
  };

  const openDocument = url => {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (isLoadingProcurement && !procurement) {
    return (
      <div className='text-center py-5'>
        <Spinner color='primary' />
        <p className='text-muted mt-2 mb-0'>Loading HPS data...</p>
      </div>
    );
  }

  if (!canAccessData) {
    return (
      <Card className='border shadow-sm mb-4'>
        <CardBody>
          <div className='mb-4'>
            <h5 className='fw-bold text-dark mb-1'>HPS Detail</h5>
            <p className='text-muted small mb-0'>
              Ringkasan HPS Alpha untuk evaluasi tender.
            </p>
          </div>
          <TenderNotClosedAlert featureLabel='HPS' reason={lockReason} />
        </CardBody>
      </Card>
    );
  }

  if (!hpsAvailable) {
    return (
      <Card className='border shadow-sm mb-4'>
        <CardBody>
          <div className='mb-4'>
            <h5 className='fw-bold text-dark mb-1'>HPS Detail</h5>
            <p className='text-muted small mb-0'>
              Ringkasan HPS Alpha untuk evaluasi tender.
            </p>
          </div>
          <Alert
            color='warning'
            className='d-flex align-items-start gap-2 mb-0'
          >
            <i className='bx bx-info-circle fs-18 mt-1'></i>
            <div>
              <h5 className='alert-heading fs-14 fw-bold mb-1'>
                HPS Not Available
              </h5>
              <p className='mb-0 fs-13'>
                HPS belum berstatus Available. Data HPS dapat ditampilkan
                setelah HPS di-release dari modul HPS.
              </p>
            </div>
          </Alert>
        </CardBody>
      </Card>
    );
  }

  return (
    <Row>
      <Col xs={12}>
        <Card className='border shadow-sm mb-4'>
          <CardBody>
            <Row>
              <Col xs={12}>
                <div className='mb-4'>
                  <h5 className='fw-bold text-dark mb-1'>HPS Detail</h5>
                  <p className='text-muted small'>
                    Ringkasan HPS Alpha untuk evaluasi tender.
                  </p>
                </div>
              </Col>
            </Row>
            <Row className='g-3'>
              <InfoField
                label='HPS Request Number'
                value={procurement?.hpsRequestNumber}
              />
              <InfoField
                label='Request Date'
                value={formatDateToDDMMYYYY(procurement?.hpsRequestDate) || '-'}
              />
            </Row>

            <hr className='my-4 border-secondary' />

            <Row className='g-3'>
              <InfoField
                label='Title Nota Dinas'
                value={procurement?.titleNotaDinas}
              />
              <InfoField
                label='No. Nota Dinas'
                value={procurement?.noNotaDinas}
              />
              <InfoField
                label='Date Nota Dinas'
                value={
                  procurement?.hpsRequestDate
                    ? formatDateToDDMMYYYY(procurement.hpsRequestDate)
                    : '-'
                }
              />
              <Col md={12} lg={12}>
                <div className='mb-3'>
                  <Label className='fw-semibold small text-dark mb-2'>
                    Document Nota Dinas
                  </Label>

                  <div className='border rounded-3 p-2 d-flex align-items-center justify-content-between bg-light'>
                    <div className='d-flex align-items-center overflow-hidden me-2'>
                      <div
                        className='d-flex align-items-center justify-content-center rounded bg-primary-subtle p-2 me-2 text-primary'
                        style={{
                          width: '36px',
                          height: '36px',
                          flexShrink: 0,
                        }}
                      >
                        <i className='ri-file-text-line fs-18'></i>
                      </div>

                      <span className='small text-dark fw-medium'>
                        {(() => {
                          const rawName =
                            procurement?.documentNotaDinasName ||
                            procurement?.documentNotaDinas;
                          if (!rawName) return 'Nota Dinas Document';
                          return rawName.includes('_')
                            ? rawName.substring(rawName.indexOf('_') + 1)
                            : rawName;
                        })()}
                      </span>
                    </div>

                    {procurement?.documentNotaDinasUrl && (
                      <Button
                        color='primary'
                        size='sm'
                        outline
                        className='d-flex align-items-center gap-1 btn-sm shadow-none'
                        onClick={() =>
                          openDocument(procurement.documentNotaDinasUrl)
                        }
                      >
                        <i className='ri-eye-line fs-14'></i>
                        <span>View</span>
                      </Button>
                    )}
                  </div>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <Card className='border shadow-sm mb-4'>
          <CardBody>
            <div className='mb-4'>
              <h5 className='fw-semibold mb-1'>Procurement Information</h5>
              <p className='text-muted small mb-0'>
                Procurement details linked to this HPS request.
              </p>
            </div>

            <div className='border-bottom mb-4'></div>

            <Row className='g-4'>
              <Col md={6} lg={4}>
                <div className='border-start border-2 border-primary ps-3'>
                  <Label className='fw-semibold small text-dark mb-1'>
                    Procurement Type
                  </Label>
                  <p className='mb-0 text-dark'>
                    {displayValue(procurement?.procurementTypeHeaderLabel)}
                  </p>
                </div>
              </Col>
              <Col md={6} lg={4}>
                <div className='border-start border-2 border-primary ps-3'>
                  <Label className='fw-semibold small text-dark mb-1'>
                    Title Procurement
                  </Label>
                  <p className='mb-0 text-dark'>
                    {displayValue(procurement?.titleProcurement)}
                  </p>
                </div>
              </Col>
              <Col md={6} lg={4}>
                <div className='border-start border-2 border-primary ps-3'>
                  <Label className='fw-semibold small text-dark mb-1'>
                    Proc Type
                  </Label>
                  <p className='mb-0 text-dark'>
                    {getProcurementTypeLabel(procurement?.procurementType)}
                  </p>
                </div>
              </Col>
              <Col md={6} lg={4}>
                <div className='border-start border-2 border-primary ps-3'>
                  <Label className='fw-semibold small text-dark mb-1'>
                    Contract Type
                  </Label>
                  <p className='mb-0 text-dark'>
                    {getContractTypeLabel(procurement?.contractType)}
                  </p>
                </div>
              </Col>
              <Col md={6} lg={4}>
                <div className='border-start border-2 border-primary ps-3'>
                  <Label className='fw-semibold small text-dark mb-1'>
                    {periodProps.startLabel}
                  </Label>
                  <p className='mb-0 text-dark'>{periodProps.startValue}</p>
                </div>
              </Col>
              {periodProps.endLabel && (
                <Col md={6} lg={4}>
                  <div className='border-start border-2 border-primary ps-3'>
                    <Label className='fw-semibold small text-dark mb-1'>
                      {periodProps.endLabel}
                    </Label>
                    <p className='mb-0 text-dark'>{periodProps.endValue}</p>
                  </div>
                </Col>
              )}
              <Col md={6} lg={4}>
                <div className='border-start border-2 border-primary ps-3'>
                  <Label className='fw-semibold small text-dark mb-1'>
                    Product
                  </Label>
                  <p className='mb-0 text-dark'>
                    {displayValue(procurement?.productType?.product?.name)}
                  </p>
                </div>
              </Col>
              <Col md={6} lg={4}>
                <div className='border-start border-2 border-primary ps-3'>
                  <Label className='fw-semibold small text-dark mb-1'>
                    Product Type
                  </Label>
                  <p className='mb-0 text-dark'>
                    {displayValue(procurement?.productType?.description)}
                  </p>
                </div>
              </Col>
              <Col md={6} lg={4}>
                <div className='border-start border-2 border-primary ps-3'>
                  <Label className='fw-semibold small text-dark mb-1'>
                    Submission Deadline (Jakarta Local Time)
                  </Label>
                  <p className='mb-0 text-dark'>
                    {formatProposalDeadline(
                      procurement?.proposalSubmissionDeadline
                    )}
                  </p>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <Card className='border shadow-sm'>
          <CardBody>
            <Alert
              color='info'
              className='d-flex align-items-start gap-2 border-0 bg-light text-primary'
            >
              <i className='bx bx-info-circle flex-shrink-0 mt-1'></i>
              <span className='small'>
                The detailed procurement plan information below can be
                downloaded in Excel format to be used as an attachment in the
                submission of the HPS Alpha Request Memorandum
              </span>
            </Alert>

            <div className='d-flex justify-content-between align-items-start mb-4'>
              <div>
                <h5 className='fw-semibold mb-1'>Parcel</h5>
                <p className='text-muted small mb-0'>
                  Delivery terms and incoterm from procurement planning.
                </p>
              </div>
              <Button
                color='success'
                size='sm'
                className='d-flex align-items-center gap-1'
                disabled={isExportingExcel || isLoadingParcels}
                onClick={exportParcelHpsToExcel}
              >
                {isExportingExcel ? (
                  <Spinner size='sm' />
                ) : (
                  <i className='bx bxs-file-export'></i>
                )}{' '}
                Download HPS
              </Button>
            </div>

            <HpsParcelSection
              parcels={parcels}
              isLoadingParcels={isLoadingParcels}
              procurement={procurement}
              isReadOnly
            />
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

export default TabHPS;
